import React, { ReactNode } from 'react';
import ErrorBoundary from './ErrorBoundary';

interface Props {
  children: ReactNode;
}

const DatabaseErrorBoundary: React.FC<Props> = ({ children }) => {
  const handleDatabaseError = (error: Error) => {
    console.error('Database Error:', error);
    
    // Check if it's a database-related error
    const isDatabaseError = 
      error.message.includes('IndexedDB') ||
      error.message.includes('database') ||
      error.message.includes('Dexie') ||
      error.name === 'DatabaseError';

    if (isDatabaseError) {
      // Could implement database recovery logic here
      console.warn('Database error detected - consider implementing recovery');
    }
  };

  const fallbackUI = (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
        <div className="text-center mb-4">
          <div className="text-4xl mb-2">💾</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Database Error
          </h2>
          <p className="text-gray-600 text-sm mb-4">
            There was a problem accessing your workout data. This might be due to browser storage limitations or corrupted data.
          </p>
        </div>

        <div className="space-y-3">
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Restart App
          </button>
          
          <button
            onClick={() => {
              if (window.confirm('This will delete all your workout data. Are you sure?')) {
                // Clear IndexedDB
                indexedDB.deleteDatabase('WorkoutTrackerDB');
                window.location.reload();
              }
            }}
            className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors"
          >
            Reset All Data
          </button>

          <div className="text-center">
            <p className="text-xs text-gray-500">
              Try restarting first. Only reset data as a last resort.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <ErrorBoundary
      fallback={fallbackUI}
      onError={handleDatabaseError}
      context="database operations"
    >
      {children}
    </ErrorBoundary>
  );
};

export default DatabaseErrorBoundary;